import React, { useContext, useEffect, useLayoutEffect } from "react";
import { SessionContext } from "../common/contexts/SessionContext";
import Menue from "../common/panels/Menue/Menue";
import { ManageEntryPage } from "./ManageEntryPage";
import { personsConstants } from "../api/Person";
import { sharedContentConstants } from "../api/SharedContent";
import { dataSourceConstants } from "../api/DataSource";
import { bridgeConstants } from "../api/Bridges";
import { apiKeysConstants } from "../api/ApiKey";
import { announcementsConstants } from "../api/Announcements";
import { auditLogConstants } from "../api/AuditLog";
import { iconNMRStatusConstants } from "../api/IconNMR";
import { rolesConstants } from "../api/Role";
import { useEntityCount, useGet } from "../api/BaseEntityApi";
import { methodsConstants } from "../api/Method";
import { customFieldConstants } from "../api/CustomFields/CustomFields";
import { customTypeConstants } from "../api/CustomTypes/CustomTypes";
import { attachmentsConstants } from "../api/Attachments";
import { entitiesConstants } from "../api/Entities";
import { usePermissions } from "../common/permissions/useEntityPermissions";
import { adminStatisticsConstants } from "../api/Admin";
import { dataFormatsConstants } from "../api/DataFormats";
import { customTypeTemplateConstants } from "../api/CustomTypes/CustomTypeTemplates";
import { customImportConstants, customImportLogConstants, CustomImportLogFilters } from "../api/CustomImports";
import { fileExportJobsConstants } from "../api/FileExportJobs";
import { zontalCredentialsConstants, zontalIPPConstants } from "../api/Zontal";
import { ZontalIcon } from "../Integrations/ZontalCommons";

interface ManageProps {
  children?: React.ReactNode;
}

export const Manage = ({ children }: ManageProps) => {
  const { session } = useContext(SessionContext);
  const { permissions } = usePermissions();
  const [isFolded, setIsFolded] = React.useState(false);

  // Parse "view" parameter from URL and fold menu if view=true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fullscreen = params.get("fullscreen");
    if (fullscreen === "true") {
      setIsFolded(true);
    }
  }, []);
  // IconNMR
  const { data: monitoringEnabled } = useGet<boolean>(`${iconNMRStatusConstants.resource}/monitoring_enabled`, {});

  // Custom import and custom import logs
  const { data: customImportLogsCountQuery } = useEntityCount<CustomImportLogFilters>(
    customImportLogConstants.resource,
    {
      includeCount: true,
    },
    {
      enabled: permissions.customImports.canReadIndex,
    }
  );

  useLayoutEffect(() => {
    if (!children) setIsFolded(false);
  }, [children]);

  return (
    <Menue isFolded={isFolded}>
      <Menue.Items>
        {/* <Menue.Items ref={ref}> */}
        {/* Manage */}
        <Menue.Items.Header label="Content" folded={false}>
          <Menue.Items.Item>
            <Menue.Items.Item.Link entityConstants={methodsConstants} disabled={!permissions.methods.canReadIndex} />
          </Menue.Items.Item>

          <Menue.Items.Item>
            <Menue.Items.Item.Link entityConstants={personsConstants} disabled={!permissions.persons.canReadIndex} />
          </Menue.Items.Item>

          <Menue.Items.Item>
            <Menue.Items.Item.Link entityConstants={entitiesConstants} labelOverride="Search by UUID" />
          </Menue.Items.Item>

          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={dataFormatsConstants}
              labelOverride={`Supported ${dataFormatsConstants.entityPlural}`}
            />
          </Menue.Items.Item>
        </Menue.Items.Header>

        {/* Custom fields & types */}
        <Menue.Items.Header label={"Customization"} folded={false}>
          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={customFieldConstants}
              disabled={!permissions.customFields.canReadIndex}
            />
          </Menue.Items.Item>
          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={customTypeConstants}
              disabled={!permissions.customTypes.canReadIndex}
            />
          </Menue.Items.Item>
          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={{ ...customTypeTemplateConstants, frontendIndexRoute: "templates" }}
              labelOverride="Templates"
              disabled={!session?.permissions.administer_custom_field_schemas}
            />
          </Menue.Items.Item>
        </Menue.Items.Header>

        {/* TOOLS */}
        <Menue.Items.Header label="Tools" folded={false}>
          <Menue.Items.Item>
            <Menue.Items.Item.Link entityConstants={apiKeysConstants} />
          </Menue.Items.Item>

          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={fileExportJobsConstants}
              disabled={!permissions.fileExportJobs.canReadIndex}
            />
          </Menue.Items.Item>

          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={sharedContentConstants}
              disabled={!permissions.sharedContents.canReadIndex}
            />
          </Menue.Items.Item>
        </Menue.Items.Header>

        <Menue.Items.Header label="Data import" folded={false}>
          <Menue.Items.Item>
            <Menue.Items.Item.Link entityConstants={bridgeConstants} disabled={!permissions.bridges.canReadIndex} />
          </Menue.Items.Item>

          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={dataSourceConstants}
              disabled={!permissions.dataSources.canReadIndex}
            />
          </Menue.Items.Item>

          {permissions.customImports.canReadIndex && (
            <>
              <Menue.Items.Item>
                <Menue.Items.Item.Link
                  entityConstants={customImportConstants}
                  disabled={!permissions.customImports.canReadIndex}
                />
              </Menue.Items.Item>
              {!!customImportLogsCountQuery?.count && (
                <Menue.Items.Item>
                  <Menue.Items.Item.Link
                    entityConstants={customImportLogConstants}
                    disabled={!permissions.customImports.canReadIndex}
                  />
                  <div className="flex align-center">
                    <div className="label label-danger" style={{ fontSize: "1rem" }}>
                      {customImportLogsCountQuery.count}
                    </div>
                  </div>
                </Menue.Items.Item>
              )}
            </>
          )}
        </Menue.Items.Header>

        {/* Monitoring */}
        {(!!session?.permissions.administer_autoload || !!monitoringEnabled) && (
          <Menue.Items.Header label="Monitoring" folded={false}>
            <Menue.Items.Item>
              <Menue.Items.Item.Link
                entityConstants={iconNMRStatusConstants}
                labelOverride="IconNMR"
                disabled={!permissions.iconNMRHistoryEntry.canReadIndex}
              />
            </Menue.Items.Item>
          </Menue.Items.Header>
        )}

        {/* Admin tools */}
        <Menue.Items.Header label="Admin" folded={false}>
          {(!!session?.permissions.view_admin_pages ||
            !!session?.permissions.logsadmin ||
            !!session?.permissions.view_global_audit_log) && (
            <Menue.Items.Item>
              <Menue.Items.Item.Link entityConstants={adminStatisticsConstants} />
            </Menue.Items.Item>
          )}
          {session?.permissions.logsadmin &&
            (session?.features.parser_development_mode || session?.features.activate_devel_parsers) && (
              <Menue.Items.Item>
                <Menue.Items.Item.Link
                  entityConstants={{
                    frontendIndexRoute: "parser_selections",
                    entityPlural: "Parser selections",
                    entitySingular: "Parser selection",
                    icon: "swatch-book",
                    resource: "",
                  }}
                  disabled={!permissions.auditLogs.canReadIndex}
                />
              </Menue.Items.Item>
            )}

          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={announcementsConstants}
              disabled={!permissions.announcements.canReadIndex}
            />
          </Menue.Items.Item>

          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={attachmentsConstants}
              disabled={!permissions.attachments.canReadIndex}
            />
          </Menue.Items.Item>

          {/* TODO: REMOVE CONDITION ONCE RELEASED */}
          {(session?.permissions.view_global_audit_log || session?.permissions.view_admin_pages) && (
            <Menue.Items.Item>
              <Menue.Items.Item.Link entityConstants={auditLogConstants} />
            </Menue.Items.Item>
          )}

          <Menue.Items.Item>
            <Menue.Items.Item.Link
              entityConstants={{
                frontendIndexRoute: "maintenance",
                entityPlural: "Maintenance",
                entitySingular: "Maintenance",
                icon: "construction",
                resource: "parsing/errors",
              }}
              labelOverride="Dataset maintenance"
              disabled={
                ![
                  session?.permissions.maintenance,
                  session?.permissions.view_admin_pages,
                  session?.features.activate_maintenance,
                ].some((p) => !!p === true)
              }
            />
          </Menue.Items.Item>

          <Menue.Items.Item>
            <Menue.Items.Item.Link entityConstants={rolesConstants} disabled={!permissions.roles.canReadIndex} />
          </Menue.Items.Item>

          {(!!session?.features.enable_zontal_integration || !!session?.features.enable_zontal_automatic_data_push) && (
            <Menue.Items.Item>
              <Menue.Items.Item.Link
                entityConstants={zontalCredentialsConstants}
                iconOverride={<ZontalIcon />}
                disabled={
                  ![
                    session?.permissions.view_admin_pages,
                    session?.features.enable_zontal_integration || session?.features.enable_zontal_automatic_data_push,
                  ].some((p) => !!p === true)
                }
              />
            </Menue.Items.Item>
          )}
        </Menue.Items.Header>

        {!!session?.features.enable_zontal_integration && (
          <Menue.Items.Header label="Integration" folded={false}>
            <Menue.Items.Item>
              <Menue.Items.Item.Link
                entityConstants={zontalIPPConstants}
                iconOverride={<ZontalIcon />}
                disabled={
                  ![session?.permissions.view_admin_pages, session?.features.enable_zontal_integration].some(
                    (p) => !!p === true
                  )
                }
              />
            </Menue.Items.Item>
          </Menue.Items.Header>
        )}
      </Menue.Items>
      <Menue.Content>{children ? children : <ManageEntryPage />}</Menue.Content>
    </Menue>
  );
};
